import React, { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Tooltip,
  Collapse,
  Divider,
  useMediaQuery,
  useTheme,
  Popover,
  Checkbox,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import InfiniteScroll from "react-infinite-scroll-component";
import { useEffect } from "react";
import {
  ProfileCreate,
  ProfileList,
  ProfileDelete,
  ProfileUpdate,
  UpdateUserStatus,
  UpdateUserRole,
} from "../../../Services/Services";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const RoleManagementTable = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [expandedProfile, setExpandedProfile] = useState(null);
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("");
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [editIndex, setEditIndex] = useState(null);
  const [errors, setErrors] = useState({});
  const [toggleAnchorEl, setToggleAnchorEl] = useState(null);
  const [userToToggle, setUserToToggle] = useState({ id: null, isActive: false });
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    address: "",
    phone_no: "",
    password: "",
  });
  const [roleAnchorEl, setRoleAnchorEl] = useState(null);
  const [userToChangeRole, setUserToChangeRole] = useState({ id: null, newRole: "" });

  const truncateText = (text, limit = 20) => {
    if (!text) return "";
    return text.length > limit ? text.substring(0, limit) + "..." : text;
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setPage(1);
      fetchProfiles(1, true, filter);
    }, 500);
    return () => clearTimeout(handler);
  }, [filter]);

  const fetchProfiles = async (pageNumber = 1, reset = false, search = "") => {
    try {
      const data = await ProfileList(pageNumber, search);
      if (reset) {
        setRows(data.results || []);
      } else {
        setRows((prev) => [...prev, ...(data.results || [])]);
      }
      setHasMore(Boolean(data.next));
      setPage(pageNumber);
    } catch (error) {
      toast.error("Failed to load profiles");
    }
  };

  const fetchMoreData = () => {
    if (hasMore) fetchProfiles(page + 1);
  };

  const handleToggleActive = async (id, checked) => {
    try {
      const updatedRows = [...rows];
      const index = updatedRows.findIndex((user) => user.id === id);
      if (index !== -1) updatedRows[index].is_active = checked;
      setRows(updatedRows);
      const response = await UpdateUserStatus(id, checked);
      toast.success(response?.message || "User status updated successfully");
    } catch (error) {
      toast.error("Error updating user status");
    }
  };

  const handleAddNewRow = async () => {
    const { name, email, address, phone_no, password } = formData;
    const newErrors = {};
    if (!name.trim()) newErrors.name = "Name is required";
    if (!email.trim()) newErrors.email = "Email is required";
    if (!address.trim()) newErrors.address = "Address is required";
    if (!phone_no.trim()) newErrors.phone_no = "Phone number is required";
    if (!password.trim()) newErrors.password = "Password is required";
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    try {
      const response = await ProfileCreate({ name, email, address, phone_no, password });
      setRows([...rows, response]);
      setFormData({ name: "", email: "", address: "", phone_no: "", password: "" });
      setErrors({});
      setOpenAddModal(false);
      toast.success("Profile added successfully!");
    } catch (error) {
      toast.error("Failed to add profile.");
    }
  };

  const handleDelete = async (id) => {
    try {
      await ProfileDelete(id);
      setRows(rows.filter((user) => user.id !== id));
      toast.info("Profile deleted.");
    } catch (error) {
      toast.error("Failed to delete profile.");
    }
  };

  const validateEditForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.email.trim()) newErrors.email = "Email is required";
    if (!formData.address.trim()) newErrors.address = "Address is required";
    if (!formData.phone_no.trim()) newErrors.phone_no = "Phone number is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleUpdateRow = async () => {
    if (editIndex !== null && validateEditForm()) {
      try {
        const profileId = rows[editIndex].id;
        const response = await ProfileUpdate(profileId, formData);
        setRows(rows.map((user) => (user.id === profileId ? { ...user, ...response } : user)));
        setEditIndex(null);
        setOpenEditModal(false);
        toast.success("Profile updated successfully!");
      } catch (error) {
        toast.error("Failed to update profile.");
      }
    }
  };

  const handleToggleSubAdmin = async (id, makeSubAdmin) => {
    try {
      const newRole = makeSubAdmin ? "SubAdmin" : "Staff";
      setRows((prev) => prev.map((user) => (user.id === id ? { ...user, role: newRole } : user)));
      const response = await UpdateUserRole(id, newRole);
      toast.success(response?.message || `Role updated to ${newRole}`);
    } catch (error) {
      setRows((prev) =>
        prev.map((user) =>
          user.id === id ? { ...user, role: makeSubAdmin ? "Staff" : "SubAdmin" } : user
        )
      );
      toast.error("Error updating user role");
    }
  };

  const userRole = localStorage.getItem("role");

  // ---------- Mobile Card Component ----------
  const MobileProfileCard = ({ row, index }) => {
    const isExpanded = expandedProfile === row.id;
    const isAdmin = row?.role?.toLowerCase() === "admin";
    return (
      <Box>
        <Box
          onClick={() => setExpandedProfile(isExpanded ? null : row.id)}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 1.5,
            cursor: "pointer",
            bgcolor: !row.is_active
              ? "#ffebee"
              : index % 2 === 0
              ? "#fafafa"
              : "#ffffff",
            "&:active": { bgcolor: "#e3f2fd" },
            borderLeft: `4px solid ${!row.is_active ? "#f44336" : row.is_active ? "#4caf50" : "#999"}`,
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <Typography sx={{ fontSize: "0.7rem", color: "#666", minWidth: 25 }}>#{index + 1}</Typography>
              <Typography sx={{ fontWeight: "bold", fontSize: "0.85rem", flex: 1 }}>{row.name}</Typography>
            </Box>
            <Typography sx={{ fontSize: "0.75rem", color: "#666", display: "flex", alignItems: "center", gap: 1 }}>
              <span style={{ fontWeight: 500 }}>{truncateText(row.email, 20)}</span>
              <span style={{ color: "#999" }}>•</span>
              <span style={{ fontSize: "0.7rem" }}>{row.phone_no}</span>
            </Typography>
          </Box>
          <IconButton size="small">{isExpanded ? <ExpandLess /> : <ExpandMore />}</IconButton>
        </Box>
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <Box
            sx={{
              px: 1.5,
              py: 1,
              bgcolor: !row.is_active
                ? "#ffebee"
                : index % 2 === 0
                ? "#f8f9fa"
                : "#fafafa",
              borderLeft: `4px solid ${!row.is_active ? "#f44336" : row.is_active ? "#4caf50" : "#999"}`,
            }}
          >
            <Box sx={{ mb: 0.8 }}>
              <Typography sx={{ fontSize: "0.6rem", color: "#999", letterSpacing: "0.5px", mb: 0.3 }}>EMAIL</Typography>
              <Typography sx={{ fontSize: "0.72rem", fontWeight: 500, color: "#555" }}>{row.email}</Typography>
            </Box>
            <Box sx={{ mb: 0.8 }}>
              <Typography sx={{ fontSize: "0.6rem", color: "#999", letterSpacing: "0.5px", mb: 0.3 }}>ADDRESS</Typography>
              <Typography sx={{ fontSize: "0.7rem", color: "#555", lineHeight: 1.3 }}>{row.address}</Typography>
            </Box>
            <Box sx={{ mb: 0.8 }}>
              <Typography sx={{ fontSize: "0.6rem", color: "#999", letterSpacing: "0.5px", mb: 0.3 }}>PHONE NUMBER</Typography>
              <Typography sx={{ fontSize: "0.72rem", fontWeight: 500, color: "#555" }}>{row.phone_no}</Typography>
            </Box>
            <Box sx={{ mb: 0.8 }}>
              <Typography sx={{ fontSize: "0.6rem", color: "#999", letterSpacing: "0.5px", mb: 0.3 }}>STATUS</Typography>
              <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: row.is_active ? "#2e7d32" : "#c62828" }}>
                {row.is_active ? "Active" : "Inactive"}
              </Typography>
            </Box>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
              {userRole === "Admin" && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, bgcolor: row.is_active ? "#e8f5e9" : "#ffebee", px: 1, py: 0.5, borderRadius: "6px" }}>
                  <Checkbox
                    checked={row.is_active}
                    onChange={(e) => {
                      e.stopPropagation();
                      setUserToToggle({ id: row.id, isActive: e.target.checked });
                      setToggleAnchorEl(e.currentTarget);
                    }}
                    sx={{ padding: 0, transform: "scale(0.75)" }}
                  />
                  <Typography sx={{ fontSize: "0.7rem", fontWeight: 600, color: row.is_active ? "#2e7d32" : "#c62828" }}>
                    {row.is_active ? "Active" : "Inactive"}
                  </Typography>
                </Box>
              )}
              <Box sx={{ display: "flex", gap: 0.6 }}>
                <IconButton
                  size="small"
                  disabled={isAdmin}
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditIndex(index);
                    setFormData(row);
                    setOpenEditModal(true);
                  }}
                  sx={{ bgcolor: isAdmin ? "#f5f5f5" : "#e8f5e9", borderRadius: "6px", width: 32, height: 32 }}
                >
                  <EditIcon sx={{ fontSize: "1rem", color: isAdmin ? "#bdbdbd" : "#4caf50" }} />
                </IconButton>
                <IconButton
                  size="small"
                  disabled={isAdmin}
                  onClick={(e) => {
                    e.stopPropagation();
                    setAnchorEl(e.currentTarget);
                    setProjectToDelete(row.id);
                  }}
                  sx={{ bgcolor: isAdmin ? "#f5f5f5" : "#ffebee", borderRadius: "6px", width: 32, height: 32 }}
                >
                  <DeleteIcon sx={{ fontSize: "1rem", color: isAdmin ? "#bdbdbd" : "#f44336" }} />
                </IconButton>
              </Box>
            </Box>
          </Box>
        </Collapse>
        <Divider />
      </Box>
    );
  };

  // ---------- MAIN RENDER ----------
  return (
    // ✅ Only change: added top margin on mobile (xs: 2), none on desktop (md: 0)
    <Box sx={{ mt: { xs: 4, md: 0 }, px: { xs: 1, sm: 0 } }}>
      {/* Search + Add Button */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          gap: 2,
          mb: 2,
        }}
      >
        <TextField
          label="Search by name"
          variant="outlined"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          fullWidth
          size="small"
          sx={{ flex: 1, "& .MuiInputLabel-root": { fontSize: "0.75rem" } }}
        />
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            setFormData({ name: "", email: "", address: "", phone_no: "", password: "" });
            setErrors({});
            setOpenAddModal(true);
          }}
          sx={{ height: 40, minWidth: 120, fontSize: "0.75rem", borderRadius: 1 }}
        >
          <AddIcon fontSize="small" sx={{ mr: 0.5 }} /> Profile
        </Button>
      </Box>

      <InfiniteScroll
        dataLength={rows.length}
        next={fetchMoreData}
        hasMore={hasMore}
        height={isMobile ? "calc(100vh - 160px)" : "80vh"}
        loader={<Typography sx={{ textAlign: "center", p: 2 }}>Loading...</Typography>}
        endMessage={<Typography sx={{ textAlign: "center", p: 2 }}>No more profiles!</Typography>}
      >
        {isMobile ? (
          <Box sx={{ bgcolor: "background.paper", pb: 2  }}>
            {rows.map((row, index) => (
              <MobileProfileCard key={row.id} row={row} index={index} />
            ))}
          </Box>
        ) : (
          /* ---------- DESKTOP TABLE – unchanged from original ---------- */
          <TableContainer
            component={Paper}
            sx={{
              maxHeight: "80vh",
              overflow: "auto",
            }}
          >
            <Table
              stickyHeader
              size="small"
              sx={{
                minWidth: 900,
                "& td, & th": {
                  textAlign: "center",
                  whiteSpace: "nowrap",
                  backgroundColor: "#f5f5f5",
                  borderBottom: "1px solid #ccc",
                },
              }}
            >
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: "#f5f5f5",
                    "& th": {
                      fontWeight: "bold",
                      color: "#333",
                      borderBottom: "1px solid #999",
                      whiteSpace: "nowrap",
                      backgroundColor: "#f5f5f5",
                      fontSize: "0.80rem",
                    },
                  }}
                >
                  {userRole === "Admin" && <TableCell>Approve</TableCell>}
                  <TableCell
                    sx={{
                      position: "sticky",
                      left: 0,
                      backgroundColor: "#f5f5f5",
                      zIndex: 3,
                    }}
                  >
                    S. No.
                  </TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Phone No</TableCell>
                  <TableCell>Address</TableCell>
                  {userRole === "Admin" && <TableCell>Sub Admin</TableCell>}
                  <TableCell
                    align="center"
                    className="no-print"
                    sx={{
                      position: "sticky",
                      right: 0,
                      backgroundColor: "#f5f5f5",
                      zIndex: 3,
                    }}
                  >
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {rows.map((row, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      backgroundColor: !row.is_active
                        ? "#ffebee"
                        : index % 2 === 0
                        ? "#fafafa"
                        : "#ffffff",
                      "&:hover": {
                        backgroundColor: !row.is_active
                          ? "#ffebee"
                          : index % 2 === 0
                          ? "#fafafa"
                          : "#ffffff",
                      },
                      "& td": {
                        padding: "4px 8px",
                        fontSize: { xs: "0.75rem", sm: "0.8125rem" },
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        fontSize: "0.8rem",
                        textOverflow: "ellipsis",
                        maxWidth: 140,
                        color: (theme) =>
                          theme.palette.mode === "dark" ? "white" : "inherit",
                        backgroundColor: (theme) =>
                          theme.palette.mode === "dark" ? "black" : "inherit",
                      },
                    }}
                  >
                    {userRole === "Admin" && (
                      <TableCell>
                        <Checkbox
                          checked={row.is_active}
                          onChange={(e) => {
                            setUserToToggle({
                              id: row.id,
                              isActive: e.target.checked,
                            });
                            setToggleAnchorEl(e.currentTarget);
                          }}
                          color="success"
                          size="small"
                          sx={{ padding: 0, transform: "scale(0.6)" }}
                        />
                      </TableCell>
                    )}

                    <TableCell
                      sx={{
                        position: "sticky",
                        left: 0,
                        zIndex: 0,
                      }}
                    >
                      {index + 1}
                    </TableCell>

                    <TableCell>{row.name}</TableCell>
                    <TableCell>
                      <Tooltip title={row.email} arrow>
                        <span>{truncateText(row.email, 20)}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{row.phone_no}</TableCell>
                    <TableCell>
                      <Tooltip title={row.address} arrow>
                        <span>{truncateText(row.address, 20)}</span>
                      </Tooltip>
                    </TableCell>
                    {userRole === "Admin" && (
                      <TableCell>
                        {row?.role?.toLowerCase() !== "admin" ? (
                          <Tooltip
                            title={
                              row.role === "SubAdmin"
                                ? "Remove Sub Admin"
                                : "Make Sub Admin"
                            }
                            arrow
                          >
                            <Checkbox
                              checked={row.role === "SubAdmin"}
                              onChange={(e) => {
                                setUserToChangeRole({
                                  id: row.id,
                                  newRole: e.target.checked
                                    ? "SubAdmin"
                                    : "Staff",
                                });
                                setRoleAnchorEl(e.currentTarget);
                              }}
                              size="small"
                              sx={{
                                padding: 0,
                                transform: "scale(0.6)",
                                color:
                                  row.role === "SubAdmin" ? "#ff9800" : "#999",
                                "&.Mui-checked": { color: "#ff9800" },
                              }}
                            />
                          </Tooltip>
                        ) : (
                          <Typography
                            sx={{
                              fontSize: "0.7rem",
                              color: "#1565c0",
                              fontWeight: 600,
                            }}
                          >
                            Admin
                          </Typography>
                        )}
                      </TableCell>
                    )}

                    <TableCell
                      align="center"
                      className="no-print"
                      sx={{
                        position: "sticky",
                        right: 0,
                        zIndex: 0,
                      }}
                    >
                      <IconButton
                        sx={{ padding: 0, paddingLeft: 1, paddingRight: 1 }}
                        onClick={() => {
                          setEditIndex(index);
                          setFormData(row);
                          setOpenEditModal(true);
                        }}
                        disabled={row?.role?.toLowerCase() === "admin"}
                        color="primary"
                        size="small"
                      >
                        <EditIcon transform="scale(0.6)" />
                      </IconButton>

                      <IconButton
                        sx={{ padding: 0, paddingLeft: 1, paddingRight: 1 }}
                        color="error"
                        size="small"
                        onClick={(e) => {
                          setAnchorEl(e.currentTarget);
                          setProjectToDelete(row.id);
                        }}
                        disabled={row?.role?.toLowerCase() === "admin"}
                      >
                        <DeleteIcon transform="scale(0.6)" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </InfiniteScroll>

      {/* Popovers and Dialogs – unchanged */}
      <Popover
        open={Boolean(roleAnchorEl)}
        anchorEl={roleAnchorEl}
        onClose={() => setRoleAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Box sx={{ p: 1.5, minWidth: 200 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {userToChangeRole.newRole === "SubAdmin"
              ? "Make this user Sub Admin?"
              : "Remove Sub Admin role?"}
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
            <Button
              color="warning"
              size="small"
              onClick={() => {
                handleToggleSubAdmin(userToChangeRole.id, userToChangeRole.newRole === "SubAdmin");
                setRoleAnchorEl(null);
                setUserToChangeRole({ id: null, newRole: "" });
              }}
            >
              Yes
            </Button>
            <Button size="small" onClick={() => setRoleAnchorEl(null)}>No</Button>
          </Box>
        </Box>
      </Popover>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Box sx={{ p: 1 }}>
          <Typography variant="body2">Are you sure?</Typography>
          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
            <Button color="error" size="small" onClick={() => { handleDelete(projectToDelete); setAnchorEl(null); setProjectToDelete(null); }}>Yes</Button>
            <Button size="small" onClick={() => setAnchorEl(null)}>No</Button>
          </Box>
        </Box>
      </Popover>

      <Popover
        open={Boolean(toggleAnchorEl)}
        anchorEl={toggleAnchorEl}
        onClose={() => setToggleAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Box sx={{ p: 1 }}>
          <Typography variant="body2">Are you sure you want to {userToToggle.isActive ? "activate" : "deactivate"}?</Typography>
          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
            <Button color="error" size="small" onClick={() => { handleToggleActive(userToToggle.id, userToToggle.isActive); setToggleAnchorEl(null); setUserToToggle({ id: null, isActive: false }); }}>Yes</Button>
            <Button size="small" onClick={() => setToggleAnchorEl(null)}>No</Button>
          </Box>
        </Box>
      </Popover>

      <Dialog open={openAddModal} onClose={() => setOpenAddModal(false)}>
        <DialogTitle>Add New Profile</DialogTitle>
        <DialogContent>
          <TextField label="Name" fullWidth margin="dense" value={formData.name} onChange={(e) => { setFormData({ ...formData, name: e.target.value }); setErrors({ ...errors, name: "" }); }} error={Boolean(errors.name)} helperText={errors.name} />
          <TextField label="Email" fullWidth margin="dense" value={formData.email} onChange={(e) => { setFormData({ ...formData, email: e.target.value }); setErrors({ ...errors, email: "" }); }} error={Boolean(errors.email)} helperText={errors.email} />
          <TextField label="Address" fullWidth margin="dense" value={formData.address} onChange={(e) => { setFormData({ ...formData, address: e.target.value }); setErrors({ ...errors, address: "" }); }} error={Boolean(errors.address)} helperText={errors.address} />
          <TextField label="Phone No" fullWidth margin="dense" value={formData.phone_no} onChange={(e) => { const input = e.target.value; if (/^\d{0,10}$/.test(input)) { setFormData({ ...formData, phone_no: input }); setErrors({ ...errors, phone_no: "" }); } }} error={Boolean(errors.phone_no)} helperText={errors.phone_no} />
          <TextField label="Password" fullWidth margin="dense" type="password" value={formData.password} onChange={(e) => { setFormData({ ...formData, password: e.target.value }); setErrors({ ...errors, password: "" }); }} error={Boolean(errors.password)} helperText={errors.password} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddModal(false)} color="error">Close</Button>
          <Button onClick={handleAddNewRow} color="primary">Add</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openEditModal} onClose={() => setOpenEditModal(false)}>
        <DialogTitle>Edit Record</DialogTitle>
        <DialogContent>
          <TextField label="Name" fullWidth margin="dense" value={formData.name} onChange={(e) => { setFormData({ ...formData, name: e.target.value }); setErrors((prev) => ({ ...prev, name: "" })); }} error={Boolean(errors.name)} helperText={errors.name} />
          <TextField label="Email" fullWidth margin="dense" value={formData.email} onChange={(e) => { setFormData({ ...formData, email: e.target.value }); setErrors((prev) => ({ ...prev, email: "" })); }} error={Boolean(errors.email)} helperText={errors.email} />
          <TextField label="Address" fullWidth margin="dense" value={formData.address} onChange={(e) => { setFormData({ ...formData, address: e.target.value }); setErrors((prev) => ({ ...prev, address: "" })); }} error={Boolean(errors.address)} helperText={errors.address} />
          <TextField label="Phone No" fullWidth margin="dense" value={formData.phone_no} onChange={(e) => { const input = e.target.value; if (/^\d{0,10}$/.test(input)) { setFormData({ ...formData, phone_no: input }); setErrors((prev) => ({ ...prev, phone_no: "" })); } }} error={Boolean(errors.phone_no)} helperText={errors.phone_no} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditModal(false)} color="error">Close</Button>
          <Button onClick={handleUpdateRow} color="primary">Update</Button>
        </DialogActions>
      </Dialog>

      <ToastContainer position="top-right" autoClose={3000} />
    </Box>
  );
};

export default RoleManagementTable;